import React from "react";
import * as MUI from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { CgTemplate } from "react-icons/cg";
import { useTranslation } from "react-i18next";
import { SnackbarContext } from "../../../../contexts/Snackbar";
import TemplateDetails from "../singleView/components/TemplateDetails";

// Mirrors WarehouseServiceDetailsDialog.jsx's header exactly: action icons
// on the left, X-close only on the right - no GoBack (it's a dialog now,
// nothing to go back to) and no Save icon (view-only; Edit opens the form
// dialog instead, same as Items/Services). Copy is TemplateView.jsx's old
// clipboard-copy-description action, kept and relocated into the icon row
// rather than dropped.
export default function TemplateDetailsDialog({
  template,
  canEdit,
  canDelete,
  onClose,
  onEdit,
  onDelete,
}) {
  const { t } = useTranslation();
  const theme = MUI.useTheme();
  const fullScreen = MUI.useMediaQuery(theme.breakpoints.down("md"));
  const { handleSnackbar } = React.useContext(SnackbarContext);

  function handleCopy() {
    try {
      const textWithoutHtml = (template?.description ?? "").replace(/<[^>]+>/g, "");
      navigator.clipboard.writeText(textWithoutHtml);
      handleSnackbar("CopiedToClipBoard", "info");
    } catch (err) {
      handleSnackbar("SomethingWentWrong", "error");
    }
  }

  return (
    <MUI.Dialog
      open={Boolean(template)}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
    >
      <MUI.DialogTitle sx={{ p: { xs: 2.5, sm: 3.5 }, pt: { xs: 1, sm: 1.25 } }}>
        <MUI.Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <MUI.Stack direction="row" spacing={0.5}>
            {canDelete && (
              <MUI.Tooltip title={t("Delete")}>
                <MUI.IconButton
                  onClick={() => onDelete(template)}
                  size="small"
                  color="error"
                  data-testid="template.details-dialog.delete-trigger"
                >
                  <DeleteIcon fontSize="small" />
                </MUI.IconButton>
              </MUI.Tooltip>
            )}
            {canEdit && (
              <MUI.Tooltip title={t("Edit")}>
                <MUI.IconButton
                  onClick={() => onEdit(template)}
                  size="small"
                  data-testid="template.details-dialog.edit-trigger"
                >
                  <EditIcon fontSize="small" />
                </MUI.IconButton>
              </MUI.Tooltip>
            )}
            <MUI.Tooltip title={t("Copy")}>
              <MUI.IconButton
                onClick={handleCopy}
                size="small"
                data-testid="template.details-dialog.copy-trigger"
              >
                <ContentCopyIcon fontSize="small" />
              </MUI.IconButton>
            </MUI.Tooltip>
          </MUI.Stack>

          <MUI.IconButton
            onClick={onClose}
            size="small"
            data-testid="template.details-dialog.close-trigger"
          >
            <CloseIcon fontSize="small" />
          </MUI.IconButton>
        </MUI.Stack>

        <MUI.Stack direction="row" spacing={2} alignItems="center">
          <MUI.Avatar
            variant="rounded"
            sx={{
              bgcolor: "primary.main",
              width: 52,
              height: 52,
              borderRadius: 2.5,
            }}
          >
            <CgTemplate />
          </MUI.Avatar>
          <MUI.Box sx={{ minWidth: 0 }}>
            <MUI.Typography
              variant="h6"
              sx={{ fontWeight: 700, lineHeight: 1.25 }}
              noWrap
              data-testid="template.details-dialog.title"
            >
              {template?.title}
            </MUI.Typography>
            <MUI.Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 0.5 }}
            >
              <MUI.Chip
                label={`${t("ID")}: ${template?.id}`}
                size="small"
                color="primary"
              />
            </MUI.Stack>
          </MUI.Box>
        </MUI.Stack>
      </MUI.DialogTitle>

      <MUI.DialogContent
        dividers
        data-testid="template.details-dialog.content"
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          bgcolor: (theme) =>
            theme.palette.mode === "light"
              ? "grey.50"
              : "rgba(255,255,255,0.02)",
        }}
      >
        <TemplateDetails template={template} />
      </MUI.DialogContent>
    </MUI.Dialog>
  );
}